import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { UserProfile } from '@reqshare/shared';
import { getGoogleAuthUrl, getGithubAuthUrl } from '../../api/auth-api';
import './AuthControls.scss';

const RETURN_URL_KEY = 'reqshare_auth_return';

interface AuthControlsProps {
  user: UserProfile | null;
  isAuthenticated: boolean;
  loading: boolean;
  onLogout: () => void;
}

export function AuthControls({ user, isAuthenticated, loading, onLogout }: AuthControlsProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) return null;

  function storeReturnUrl() {
    sessionStorage.setItem(RETURN_URL_KEY, location.pathname + location.search);
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-controls" ref={menuRef}>
        <button
          className="auth-controls__sign-in"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          Sign in
        </button>
        {menuOpen && (
          <div className="auth-controls__dropdown">
            <a
              href={getGoogleAuthUrl({ source: 'web' })}
              className="auth-controls__provider"
              onClick={storeReturnUrl}
            >
              Continue with Google
            </a>
            <a
              href={getGithubAuthUrl({ source: 'web' })}
              className="auth-controls__provider"
              onClick={storeReturnUrl}
            >
              Continue with GitHub
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="auth-controls" ref={menuRef}>
      <button
        className="auth-controls__user"
        onClick={() => setMenuOpen(!menuOpen)}
      >
        {user?.email || 'Account'}
      </button>
      {menuOpen && (
        <div className="auth-controls__dropdown">
          <Link
            to="/shared-requests"
            className="auth-controls__menu-item"
            onClick={() => setMenuOpen(false)}
          >
            Shared requests
          </Link>
          <Link
            to="/preferences"
            className="auth-controls__menu-item"
            onClick={() => setMenuOpen(false)}
          >
            Preferences
          </Link>
          <button
            className="auth-controls__menu-item"
            onClick={() => { onLogout(); setMenuOpen(false); }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
